import React, { Suspense, lazy } from "react";
import PropTypes from "prop-types";
import { LazyMotion, domAnimation } from "framer-motion";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { Provider, useSelector } from "react-redux";
import store from "./redux/store";
import "./i18n";
import logo from "./assets/logo.png";

// Core components that should load immediately (above the fold)
import PageHeader from "./componentes/PageHeader";
import Footer from "./componentes/Footer";
import ScrollButton from "./componentes/ScrollButton/";
import ThemeToggle from "./componentes/ThemeToggle";
import ContactIcon from "./pages/Home/ContactIcon";
import MetaTags from "./componentes/MetaTags";
import { resolveMeta } from "./utils/routeMeta";
import {
  CommunityAuthProvider,
  useCommunityAuth,
} from "./contexts/CommunityAuthContext";

// Loading fallback component - Full screen overlay to prevent FOUC
const PageLoader = () => (
  <div className="fixed inset-0 bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 z-50 flex items-center justify-center">
    <div className="text-center">
      {/* Logo with subtle pulse */}
      <div className="mb-8 animate-[pulse_2s_ease-in-out_infinite]">
        <img
          src={logo}
          alt="EthioCERT Logo"
          className="h-20 w-auto mx-auto drop-shadow-lg"
        />
      </div>

      {/* Modern animated dots loader */}
      <div className="flex justify-center items-center gap-2">
        <div className="w-3 h-3 bg-primary rounded-full animate-[bounce_1s_ease-in-out_infinite]"></div>
        <div className="w-3 h-3 bg-primary rounded-full animate-[bounce_1s_ease-in-out_0.1s_infinite]"></div>
        <div className="w-3 h-3 bg-primary rounded-full animate-[bounce_1s_ease-in-out_0.2s_infinite]"></div>
      </div>
    </div>
  </div>
);

// =====================================================
// PAGES - Loaded immediately
// =====================================================

// Home
const MainHome = lazy(() => import("./pages/Home/MainHome"));

// Incident Reports
import ReportLanding from "./pages/Incident Report/ReportLanding";
import SteppedIncidentForm from "./pages/Incident Report/SteppedIncidentForm";
import SteppedCOPForm from "./pages/Incident Report/SteppedCOPForm";
import SteppedVulnerabilityForm from "./pages/Incident Report/SteppedVulnerabilityForm";
import SteppedMalwareForm from "./pages/Incident Report/SteppedMalwareForm";
import SteppedAbuseForm from "./pages/Incident Report/SteppedAbuseForm";
import TrackReport from "./pages/Incident Report/TrackReport";

// About Us
import AboutEthioCert from "./pages/About us/AboutEthioCert";
import OurService from "./pages/About us/OurService";
import RFC2350 from "./pages/About us/RFC2350";
import Faq from "./pages/About us/Faq";
import ServiceDetails from "./pages/About us/ServiceDetails";
import ProductDetails from "./pages/About us/ProductDetails";

// News and Events
import News from "./pages/News and Events/News";
import NewsDetail from "./pages/News and Events/NewsDetail";
import Events from "./pages/News and Events/Events";
import Alerts from "./pages/News and Events/Alerts";
import Advisories from "./pages/News and Events/Advisories";
import AlertDetails from "./pages/News and Events/AlertDetails";
import AdvisoriesDetails from "./pages/News and Events/AdvisoriesDetails";
import EventDetails from "./pages/News and Events/EventDetails";
import CVE from "./pages/News and Events/CVE";

// Resources
import Tools from "./pages/Resources/Tools";
import Reports from "./pages/Resources/Reports";
import Regulations from "./pages/Resources/Regulations";
import ChildOnlineProtection from "./pages/Resources/ChildOnlineProtection";

// Awareness and Training
import AwarenessDetail from "./pages/Awareness and Training/AwarenessDetail";
import IndividualTraining from "./pages/Awareness and Training/IndividualTraining";
import PublicPrivateTraining from "./pages/Awareness and Training/PublicPrivateTraining";
import ProfessionalTraining from "./pages/Awareness and Training/ProfessionalTraining";
import FamilyTraining from "./pages/Awareness and Training/FamilyTraining";
import YouthTraining from "./pages/Awareness and Training/YouthTraining";

// Contact
import ContactUs from "./pages/Contact Us/ContactUs";

// Community
import CommunityLayout from "./components/community/shared/Layout";
import CommunityRedirect from "./components/community/shared/CommunityRedirect";
import Login from "./components/community/Auth/Login";
import Register from "./components/community/Auth/Register";
import VerifyOTP from "./components/community/Auth/VerifyOTP";
import VerifyEmailChangeOTP from "./components/community/Auth/VerifyEmailChangeOTP";
import ForgotPassword from "./components/community/Auth/ForgotPassword";
import ResetPassword from "./components/community/Auth/ResetPassword";
import UserProfile from "./components/community/Profile/UserProfile";
import EditProfile from "./components/community/Profile/EditProfile";

// Blog, Forum, Learning, Challenges
import BlogList from "./components/community/blog/BlogList";
import BlogDetail from "./components/community/blog/BlogDetail";
import ForumList from "./components/community/forum/ForumList";
import ForumDetail from "./components/community/forum/ForumDetail";
import LearningList from "./components/community/learning/LearningList";
import LearningDetail from "./components/community/learning/LearningDetail";
// import CTFList from "./components/community/challenges/CTFList";
import PublicProfile from "./pages/Community/PublicProfile";
import CertificateVerify from "./components/community/certificates/CertificateVerify";

// Admin
const AdminLayout = lazy(() => import("./admin/AdminLayout"));
const AdminDashboardPage = lazy(() => import("./pages/admin/Dashboard"));
const AdminProfilePage = lazy(() => import("./pages/admin/Profile"));
const AdminCreateContentPage = lazy(() => import("./pages/admin/CreateContent"));
const PostModerationPage = lazy(() => import("./pages/admin/PostModerationPage"));
const ModerationPendingPage = lazy(() => import("./pages/admin/ModerationPending"));
const ModerationReportsPage = lazy(() => import("./pages/admin/ModerationReports"));
const ContentBlogsPage = lazy(() => import("./pages/admin/ContentBlogs"));
const ContentForumsPage = lazy(() => import("./pages/admin/ContentForums"));
const ContentLearningPathsPage = lazy(() => import("./pages/admin/ContentLearningPaths"));
// const ContentChallengesPage = lazy(() => import("./pages/admin/ContentChallenges"));
const ContentNewsPage = lazy(() => import("./pages/admin/ContentNews"));
const ContentEventsPage = lazy(() => import("./pages/admin/ContentEvents"));
const ContentAlertsPage = lazy(() => import("./pages/admin/ContentAlerts"));
const ContentAdvisoriesPage = lazy(() => import("./pages/admin/ContentAdvisories"));
const ContentToolsPage = lazy(() => import("./pages/admin/ContentTools"));
const ContentReportsPage = lazy(() => import("./pages/admin/ContentReports"));
const ContentRegulationsPage = lazy(() => import("./pages/admin/ContentRegulations"));
const ContactMessagesPage = lazy(() => import("./pages/admin/ContactMessages"));
const MembersAdminPage = lazy(() => import("./pages/admin/Members"));
const CategoriesTagsAdminPage = lazy(() => import("./pages/admin/CategoriesTags"));
const AnalyticsAdminPage = lazy(() => import("./pages/admin/Analytics"));
const LegalAdminPage = lazy(() => import("./pages/admin/Legal"));

// 404
import NotFound from "./pages/NotFound";


// =====================================================
// ROUTE GUARDS
// =====================================================

const AdminRoute = ({ children }) => {
  const { isAdmin, isAuthenticated, loading } = useCommunityAuth();
  const location = useLocation();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate to="/community/login" replace state={{ from: location }} />
    );
  }

  if (!isAdmin) {
    return (
      <Navigate to="/community/profile" replace state={{ from: location }} />
    );
  }

  return children;
};

AdminRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

// =====================================================
// MAIN APP CONTENT
// =====================================================

const AppContent = () => {
  const language = useSelector((state) => state.language.selectedLanguage);
  const location = useLocation();
  const meta = resolveMeta(location.pathname);
  const [mounted, setMounted] = React.useState(false);
  const isCommunityRoute = location.pathname.startsWith('/community') || 
    location.pathname.startsWith('/blog') || 
    location.pathname.startsWith('/forum') || 
    location.pathname.startsWith('/learning') || 
    // location.pathname.startsWith('/ctf') || 
    location.pathname.startsWith('/profile');
  const isAdminArea = location.pathname.startsWith("/admin");

  // Prevent FOUC - wait for React to fully mount
  React.useEffect(() => {
    setMounted(true);
  }, []);

  // Track page views for Umami analytics (load analytics only in production)
  React.useEffect(() => {
    const doTrack = async () => {
      // Only load the real Umami tracking module in production builds
      if (import.meta.env && import.meta.env.PROD) {
        try {
          const mod = await import("./utils/umami");
          if (mod && typeof mod.trackPageView === "function") {
            mod.trackPageView(location.pathname, meta.title);
          }
        } catch (err) {
          // swallow errors from analytics loading in case of blocking or other failures
          // keep dev experience unaffected
        }
      }
    };

    doTrack();
  }, [location.pathname, meta.title]);

  // Show loader until app is mounted
  if (!mounted) {
    return <PageLoader />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <MetaTags
        {...meta}
        url={`https://www.ethiocert.gov.et${location.pathname}`}
      />
      {!isAdminArea && <PageHeader />}
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<MainHome />} />
            <Route path="/Home" element={<MainHome />} />
            <Route path="/incident-report" element={<ReportLanding />} />
            <Route path="/track-report" element={<TrackReport />} />
            <Route
              path="/report-incident/form"
              element={<SteppedIncidentForm />}
            />
            <Route
              path="/report-vulnerability/form"
              element={<SteppedVulnerabilityForm />}
            />
            <Route path="/report-cop/form" element={<SteppedIncidentForm />} />
            <Route path="/report-cop/stepped" element={<SteppedCOPForm />} />
            <Route path="/report-malware/form" element={<SteppedMalwareForm />} />
            <Route path="/report-abuse/form" element={<SteppedAbuseForm />} />
            <Route path="/report-abuse/form" element={<SteppedIncidentForm />} />
            {/* <Route path="/incident-report/legacy" element={<IncidentReportPage />} /> */}
            <Route path="/about-ethio-cert" element={<AboutEthioCert />} />
            <Route path="/our-service" element={<OurService />} />
            <Route path="/services/:serviceId" element={<ServiceDetails />} />
            <Route path="/products/:productId" element={<ProductDetails />} />
            <Route path="/rfc-2350" element={<RFC2350 />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:slug" element={<NewsDetail />} />
            <Route path="/events" element={<Events />} />
            <Route path="/events/:slug" element={<EventDetails />} />
            <Route path="/alerts" element={<Alerts lang={language} />} />
            <Route
              path="/alerts/:slug"
              element={<AlertDetails lang={language} />}
            />
            <Route path="/advisories" element={<Advisories lang={language} />} />
            <Route
              path="/advisories/:slug"
              element={<AdvisoriesDetails lang={language} />}
            />
            <Route path="/cves" element={<CVE />} />
            <Route path="/tools" element={<Tools />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/regulations" element={<Regulations />} />
            <Route
              path="/child-online-protection"
              element={<ChildOnlineProtection />}
            />
            <Route path="/individual-training" element={<IndividualTraining />} />
            <Route
              path="/awareness-detail/:group/:threatType"
              element={<AwarenessDetail />}
            />
            <Route
              path="/publicprivate-training"
              element={<PublicPrivateTraining />}
            />
            <Route
              path="/professional-training"
              element={<ProfessionalTraining />}
            />
            <Route path="/family-training" element={<FamilyTraining />} />
            <Route path="/youth-training" element={<YouthTraining />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/blog" element={<BlogList isOwn={false} />} />
            <Route path="/blog/:id" element={<BlogDetail />} />
            <Route path="/profile/:username" element={<PublicProfile />} />
            <Route path="/forum" element={<ForumList isOwn={false} />} />
            <Route path="/forum/:id" element={<ForumDetail />} />
            <Route path="/learning" element={<LearningList isOwn={false} />} />
            <Route path="/learning/:id" element={<LearningDetail />} />
            {/* <Route path="/ctf" element={<CTFList isOwn={false} />} /> */}

            <Route
              path="/admin"
              element={(
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              )}
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboardPage />} />
              <Route path="profile" element={<AdminProfilePage />} />
              <Route path="profile/edit" element={<EditProfile redirectTo="/admin/profile" />} />
              <Route path="create-content" element={<AdminCreateContentPage />} />
              <Route path="moderation/posts" element={<PostModerationPage />} />
              <Route path="moderation/pending" element={<ModerationPendingPage />} />
              <Route path="moderation/reports" element={<ModerationReportsPage />} />
              <Route path="content/blogs" element={<ContentBlogsPage />} />
              <Route path="content/forums" element={<ContentForumsPage />} />
              <Route path="content/learning-paths" element={<ContentLearningPathsPage />} />
              {/* <Route path="content/challenges" element={<ContentChallengesPage />} /> */}
              <Route path="news" element={<ContentNewsPage />} />
              <Route path="events" element={<ContentEventsPage />} />
              <Route path="alerts" element={<ContentAlertsPage />} />
              <Route path="advisories" element={<ContentAdvisoriesPage />} />
              <Route path="tools" element={<ContentToolsPage />} />
              <Route path="reports" element={<ContentReportsPage />} />
              <Route path="regulations" element={<ContentRegulationsPage />} />
              <Route path="contact" element={<ContactMessagesPage />} />
              <Route path="members" element={<MembersAdminPage />} />
              <Route path="categories-tags" element={<CategoriesTagsAdminPage />} />
              <Route path="analytics" element={<AnalyticsAdminPage />} />
              <Route path="legal" element={<LegalAdminPage />} />
            </Route>

            <Route path="/community" element={<CommunityLayout />}>
              <Route index element={<CommunityRedirect />} />
              <Route path="login" element={<Login />} />
              <Route path="register" element={<Register />} />
              <Route path="verify-otp" element={<VerifyOTP />} />
              <Route path="verify-email-change" element={<VerifyEmailChangeOTP />} />
              <Route path="forgot-password" element={<ForgotPassword />} />
              <Route path="reset-password" element={<ResetPassword />} />
              <Route path="profile" element={<UserProfile />} />
              <Route path="profile/edit" element={<EditProfile />} />
              <Route path="profile/:username" element={<UserProfile />} />
              <Route path="certificate/verify/:certificateId" element={<CertificateVerify />} />
              
              <Route path="blog" element={<BlogList isOwn={true} />} />
              <Route path="blog/:id" element={<BlogDetail />} />
              <Route path="forum" element={<ForumList isOwn={true} />} />
              <Route path="forum/:id" element={<ForumDetail />} />
              <Route path="learning" element={<LearningList isOwn={true} />} />
              <Route path="learning/:id" element={<LearningDetail />} />
              {/* <Route path="challenges" element={<CTFList isOwn={true} />} /> */}
              <Route
                path="admin"
                element={<Navigate to="/admin/dashboard" replace />}
              />
              <Route
                path="admin/reports"
                element={<Navigate to="/admin/reports" replace />}
              />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        {!isAdminArea && (
          <>
            <ScrollButton />
            <ContactIcon />
            {isCommunityRoute && <ThemeToggle />}
          </>
        )}
      </main>
      {!isAdminArea && <Footer />}
    </div>
  );
};
const App = () => (
  <Provider store={store}>
    <CommunityAuthProvider>
      <LazyMotion features={domAnimation}>
        <Router>
          <AppContent />
        </Router>
      </LazyMotion>
    </CommunityAuthProvider>
  </Provider>
);

export default App;
